/**
 * `useTeamFeatures` / `useTeamFeature` — TanStack Query hooks for
 * per-team component-toggle state (#173 PR-A4).
 *
 * Backed by `GET /api/teams/{team_id}/features` (added in PR-A4),
 * which returns one entry per component in `COMPONENT_REGISTRY` with
 * `enabled` resolved from the team's `team_features` row OR the
 * registry's `default_for_plan` fallback when no row exists.
 *
 * Two surfaces:
 *
 *   * `useTeamFeatures(teamId)` — the full list, used by PR-A5's
 *     admin UI to render category-grouped toggles.
 *   * `useTeamFeature(teamId, componentKey)` — single-component
 *     boolean, the load-bearing read for `<ComponentGate>`.
 *
 * Cache-key shape: `["team-features", teamId, accountId]`. Per-team
 * cache (admin toggles via PR-A5 invalidate this); per-account cache
 * key keeps switches between accounts clean. `accountId` is retained
 * for the query key + `enabled` guard only — the outbound auth header
 * is injected centrally by `apiFetch` via the TokenSource (#773
 * Phase 1), so the hook no longer threads `authHeaders(accountId)`.
 *
 * `staleTime: 60_000` — feature toggles are admin actions, not
 * intra-session churn. Mirrors `useTeam` / `useCycle`.
 *
 * `enabled: Boolean(teamId && accountId)` — no fetch fires for the
 * logged-out path or when the team_id hasn't been resolved yet.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { apiFetch } from "./client";

export type ComponentCategory = "foundations" | "trust_suite" | "compliance_integration";

export interface TeamFeatureItem {
  key: string;
  name: string;
  description: string;
  issue_ref: string;
  category: ComponentCategory;
  enabled: boolean;
  depends_on: string[];
}

export interface TeamFeaturesResponse {
  team_id: string;
  features: TeamFeatureItem[];
}

const STALE_TIME_MS = 60_000;

export function useTeamFeatures(teamId: string | undefined) {
  const { accountId } = useAuth();
  return useQuery({
    queryKey: ["team-features", teamId ?? "", accountId ?? ""],
    enabled: Boolean(teamId && accountId),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<TeamFeaturesResponse>(`/api/teams/${teamId}/features`),
  });
}

/**
 * Single-component boolean. Returns `undefined` while loading, `false`
 * on error or when the team has no row + no plan-default match. The
 * `<ComponentGate>` consumer reads three states (loading / on / off)
 * to drive the right UI; this hook collapses to a tri-state for
 * easier consumption:
 *
 *   * `undefined` → still loading or no team_id yet (render-children
 *     is the safe default — `<ComponentGate>` shows children while
 *     loading rather than a flicker of the off-card).
 *   * `true` → component is enabled for this team.
 *   * `false` → component is disabled, fetch errored, or the team has
 *     no row AND the plan-default resolves to false.
 */
export function useTeamFeature(
  teamId: string | undefined,
  componentKey: string,
): boolean | undefined {
  const { data, isLoading } = useTeamFeatures(teamId);
  if (isLoading || !data) return undefined;
  // Defensive: a malformed / unexpected body (no `features` array) resolves to
  // "off" rather than throwing the consumer — the backend gate is the real
  // enforcement; the SPA read is decoration.
  if (!Array.isArray(data.features)) return false;
  return data.features.find((f) => f.key === componentKey)?.enabled ?? false;
}

/**
 * `useToggleComponent` — PR-A5 admin-side mutation hook for the
 * `PATCH /api/teams/{tid}/features/{component_key}` endpoint.
 *
 * The hook returns a TanStack `UseMutationResult` so callers (the
 * `<ComponentsPage>` admin UI) can read `isPending` for spinner state
 * and `error` for cascade-conflict surfacing. On success the mutation
 * writes the updated `TeamFeaturesResponse` straight into the query
 * cache so the toggle row reflects the new state without a refetch.
 *
 * Cascade conflicts surface as `ApiError` with `status` 422 (missing
 * dependencies, on enable) or 409 (active dependents, on disable). The
 * caller is expected to inspect `error.body.error.detail` for the
 * `{kind, component_key, blockers}` envelope and prompt the admin via
 * the cascade-confirmation dialog before re-submitting with
 * `cascade: true`.
 */
export interface ToggleComponentVariables {
  componentKey: string;
  enabled: boolean;
  cascade?: boolean;
}

export function useToggleComponent(teamId: string | undefined) {
  const { accountId } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ["team-features", teamId ?? "", accountId ?? ""];

  return useMutation<
    TeamFeaturesResponse,
    Error,
    ToggleComponentVariables,
    { previous: TeamFeaturesResponse | undefined }
  >({
    mutationFn: async ({ componentKey, enabled, cascade }) =>
      apiFetch<TeamFeaturesResponse>(`/api/teams/${teamId}/features/${componentKey}`, {
        method: "PATCH",
        body: JSON.stringify({ enabled, cascade: cascade ?? false }),
      }),
    // Optimistic update: flip the toggled row in cache immediately so the
    // switch feels instant instead of waiting on the round-trip. Cancel any
    // in-flight features refetch first (so it can't clobber the optimistic
    // state), snapshot the previous response for rollback, then on error
    // (e.g. a 422/409 cascade conflict) revert before the dialog shows; on
    // success replace with the authoritative server response.
    onMutate: async ({ componentKey, enabled }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<TeamFeaturesResponse>(queryKey);
      if (previous) {
        queryClient.setQueryData<TeamFeaturesResponse>(queryKey, {
          ...previous,
          features: previous.features.map((f) => (f.key === componentKey ? { ...f, enabled } : f)),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      // The ask window (#2691) answers 404 until its component is on, so
      // re-read it once the switch has landed.
      void queryClient.invalidateQueries({ queryKey: ["clinic-cover-ask-settings", teamId] });
    },
  });
}
